// src/components/cart/CheckoutSuccessView.tsx
"use client";

import { useEffect, useMemo } from "react";
import Link from "next/link";

import Section from "@/components/ui/Section";
import Wrapper from "@/components/ui/Wrapper";
import SafeHtml from "@/components/ui/SafeHtml";
import { useLayoutData } from "@/components/layout/LayoutDataProvider";

import {
  itemMetaLines,
  lineMoney,
  resolveLocaleFromLang,
  toInt,
  formatPaymentDetailKey,
  type CartItem,
  type PaymentDetails,
  type SuccessOrder,
} from "../../lib/checkoutHelpers";

type Props = {
  lang: string;
  successOrder: SuccessOrder;
};

type TFn = (k: string, fallback?: string) => string;

function isMbway(m: string | null | undefined) {
  return m === "mbway_ifthen_for_woocommerce";
}
function isMultibanco(m: string | null | undefined) {
  return m === "multibanco_ifthen_for_woocommerce";
}
function isCCard(m: string | null | undefined) {
  return m === "creditcard_ifthen_for_woocommerce";
}
function isFree(m: string | null | undefined) {
  return m === "free_ticket";
}

function getPaymentLabel(t: TFn, m: string | null | undefined) {
  if (isMbway(m)) return t("payment_mbway", "MB WAY");
  if (isMultibanco(m)) return t("payment_multibanco", "Multibanco");
  if (isCCard(m)) return t("payment_creditcard", "Credit / debit card");
  if (isFree(m)) return t("payment_free", "No charge");
  return t("payment_default", "Payment");
}

function getHeadline(t: TFn, m: string | null | undefined, hasRedirect: boolean) {
  if (isCCard(m) && hasRedirect) return t("headline_redirecting", "Redirecting to payment…");
  return t("headline_default", "Thank you. Your order has been received.");
}

function getStatusLabel(t: TFn, m: string | null | undefined) {
  if (isMbway(m)) return t("status_awaiting_mbway", "Awaiting MB WAY approval");
  if (isMultibanco(m)) return t("status_awaiting_multibanco", "Awaiting Multibanco payment");
  return t("status_received", "Order received");
}

function getTopStatusLabel(t: TFn, m: string | null | undefined) {
  if (isMbway(m)) return t("status_awaiting_mbway", "Awaiting MB WAY approval");
  if (isMultibanco(m)) return t("status_awaiting_multibanco", "Awaiting Multibanco payment");
  return "";
}

function getPaymentInstructions(
  t: TFn,
  m: string | null | undefined,
  email: string | null | undefined,
  hasRedirect: boolean
) {
  const emailNote = email
    ? t("email_note_html", " We'll send confirmation to {email}.").replace("{email}", email)
    : "";

  if (isMbway(m)) {
    return t("instructions_mbway", "Open your MB WAY app and approve the payment request. Your order is confirmed once MB WAY approves.") + emailNote;
  }
  if (isMultibanco(m)) {
    return t("instructions_multibanco", "Pay at any Multibanco ATM or via your home banking service using the details sent by email. Your order will be confirmed once we receive payment.") + emailNote;
  }
  if (isCCard(m) && hasRedirect) {
    return t("instructions_creditcard_redirect", "You're being redirected to the payment page. Please don't close this window.");
  }
  return t("instructions_default", "Your order has been received.") + emailNote;
}

function PaymentDetailsBox({ t, details }: { t: TFn; details: PaymentDetails[] }) {
  if (details.length === 0) return null;

  return (
    <div className="mt-6 overflow-hidden rounded-2xl border">
      <div className="border-t-4 border-fuji-green bg-fuji-grey px-5 py-4">
        <div className="mb-3 font-bold">{t("payment_details_heading", "Payment details")}</div>
        <div className="flex flex-col gap-2">
          {details.map(({ key, value }) => (
            <div key={key} className="flex items-baseline justify-between gap-4 text-sm">
              <span className="shrink-0 opacity-70">{formatPaymentDetailKey(key)}</span>
              <span className="break-all text-right font-mono font-medium tracking-wide">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CheckoutSuccessView({ lang, successOrder }: Props) {
  const locale = resolveLocaleFromLang(lang);

  const { global_text } = useLayoutData();
  const SUCCESS: Record<string, string> =
    (global_text as { cart?: { success?: Record<string, string> } } | undefined)
      ?.cart?.success ?? {};
  const t: TFn = (k, fallback = "") => SUCCESS[k] || fallback;

  const { createdVia, redirectUrl, paymentDetails = [] } = successOrder;

  const orderRef = successOrder.orderNumber ?? successOrder.orderId ?? "—";
  const orderRefStr = String(orderRef);
  const shownTotal = successOrder.total ?? "—";
  const successItems: CartItem[] = Array.isArray(successOrder.items) ? successOrder.items : [];
  const shownSubtotal = successOrder.subtotal ?? "—";

  const paymentLabel = useMemo(() => getPaymentLabel(t, createdVia), [createdVia, t]);
  const headline = useMemo(() => getHeadline(t, createdVia, Boolean(redirectUrl)), [createdVia, redirectUrl, t]);
  const statusLabel = useMemo(() => getStatusLabel(t, createdVia), [createdVia, t]);
  const topStatusLabel = useMemo(() => getTopStatusLabel(t, createdVia), [createdVia, t]);
  const paymentInstructions = useMemo(
    () => getPaymentInstructions(t, createdVia, successOrder.email, Boolean(redirectUrl)),
    [createdVia, successOrder.email, redirectUrl, t]
  );

  const showPaymentDetails = isMultibanco(createdVia) && paymentDetails.length > 0;
  const isCCardOrder = useMemo(() => isCCard(createdVia), [createdVia]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (isCCardOrder && redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [isCCardOrder, redirectUrl]);

  return (
    <Section>
      <Wrapper>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-12">
            <div className="overflow-hidden">

              <div className="border-t-4 border-fuji-green bg-fuji-grey px-6 py-5 text-center">
                <div className="font-medium">{headline}</div>
                {topStatusLabel ? (
                  <div className="mt-2 text-sm opacity-80">{topStatusLabel}</div>
                ) : null}
              </div>

              <div className="py-6">
                <div className="mx-auto">

                  <div className="grid grid-cols-2 gap-6 text-sm sm:grid-cols-4">
                    <div>
                      <div className="opacity-70">{t("field_order_number", "Order number:")}</div>
                      <div className="mt-1 font-medium">{orderRefStr}</div>
                    </div>
                    <div>
                      <div className="opacity-70">{t("field_date", "Date:")}</div>
                      <div className="mt-1 font-medium">{new Date().toLocaleDateString(locale)}</div>
                    </div>
                    <div>
                      <div className="opacity-70">{t("field_total", "Total:")}</div>
                      <div className="mt-1 font-medium">{shownTotal}</div>
                    </div>
                    <div>
                      <div className="opacity-70">{t("field_payment_method", "Payment method:")}</div>
                      <div className="mt-1 font-medium">{paymentLabel}</div>
                    </div>
                  </div>

                  <div className="mt-6 text-sm opacity-80">{paymentInstructions}</div>

                  {showPaymentDetails ? <PaymentDetailsBox t={t} details={paymentDetails} /> : null}

                  {isCCardOrder && redirectUrl ? (
                    <div className="mt-4 rounded-2xl border p-4 text-sm">
                      <div className="font-medium">{t("secure_payment_heading", "Secure payment")}</div>
                      <div className="mt-1 opacity-80">
                        {redirectUrl
                          ? t("secure_payment_wait", "Please wait while you're redirected.")
                          : t("secure_payment_manual", "Your order has been placed. Complete payment on the next page.")}
                      </div>
                      {redirectUrl ? (
                        <a href={redirectUrl} className="mt-3 inline-block text-sm underline">
                          {t("secure_payment_manual_link", "Click here if you aren't redirected automatically")}
                        </a>
                      ) : null}
                    </div>
                  ) : null}

                  {isMultibanco(createdVia) ? (
                    <div className="mt-4 rounded-2xl border p-4 text-sm">
                      <div className="font-medium">{t("how_to_pay_heading", "How to pay")}</div>
                      <div className="mt-1 opacity-80">
                        {t("how_to_pay_multibanco", "Check your email for the entity, reference and payment amount. You can pay at any Multibanco ATM or via your home banking service. Your order will be automatically confirmed once we receive payment.")}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div>
                <div className="mx-auto">
                  <div className="text-lg font-bold">{t("order_details_heading", "Order details")}</div>

                  <div className="mt-4 bg-white">
                    <div className="grid grid-cols-12 border-b py-3 text-sm font-bold">
                      <div className="col-span-8">{t("table_product", "Product")}</div>
                      <div className="col-span-4 text-right">{t("table_total", "Total")}</div>
                    </div>

                    <div className="border-b py-4">
                      <div className="flex flex-col gap-4">
                        {successItems.map((it, idx) => {
                          const key = String(it?.key ?? it?.id ?? `${idx}`);
                          const name = it?.name || t("fallback_item_name", "Item");
                          const qty = toInt(it?.quantity, 1);
                          const meta = itemMetaLines(it);

                          return (
                            <div key={key} className="grid grid-cols-12 gap-3 text-sm">
                              <div className="col-span-8 min-w-0">
                                <div className="break-words font-medium [overflow-wrap:anywhere]">
                                  {it?.permalink ? (
                                    <Link href={it.permalink} className="transition-opacity hover:opacity-70">
                                      <SafeHtml content={name} />
                                    </Link>
                                  ) : (
                                    <SafeHtml content={name} />
                                  )}{" "}
                                  <span className="opacity-80">× {qty}</span>
                                </div>
                                {meta.length > 0 ? (
                                  <div className="mt-1 text-body-3 opacity-80">
                                    {meta.map((m) => (<div key={m}>{m}</div>))}
                                  </div>
                                ) : null}
                              </div>
                              <div className="col-span-4 text-right font-medium">
                                {lineMoney(it, locale)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border-b py-4 text-sm">
                      <div className="flex justify-between">
                        <span className="font-bold opacity-80">{t("subtotal_label", "Subtotal:")}</span>
                        <span className="font-medium">{shownSubtotal}</span>
                      </div>
                    </div>

                    <div className="border-b py-4 text-sm">
                      <div className="flex justify-between">
                        <span className="font-bold opacity-80">{t("field_payment_method", "Payment method:")}</span>
                        <span className="font-medium">{paymentLabel}</span>
                      </div>
                    </div>

                    <div className="border-b py-4 text-sm">
                      <div className="flex justify-between">
                        <span className="font-bold opacity-80">{t("status_label", "Status:")}</span>
                        <span className="font-medium">{statusLabel}</span>
                      </div>
                    </div>

                    <div className="py-5 text-sm">
                      <div className="flex justify-between font-bold">
                        <span>{t("field_total", "Total:")}</span>
                        <span>{shownTotal}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </Wrapper>
    </Section>
  );
}